import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dispatch, FC, SetStateAction } from "react";
import { CircleLoader } from "@/components/shared/loaders";
import { Input } from "@/components/ui/input";
import { useUserData } from "@/hooks/cache";
import { useTranslation } from "react-i18next";
import { useResendInviteEmailMutationHook } from "@/hooks/mutations";
import { toast } from "react-toastify";
import { CustomToastComponent } from "@/components/shared";
import { differenceInHours } from "date-fns";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  userId: number | undefined;
}

const SendInviteDialog: FC<Props> = (props) => {

  const { open, onOpenChange, userId } = props;

  // Hooks
  const { t } = useTranslation(["modals", "common", "toasts"]);

  // Mutation hooks
  const { resendInviteEmailMutation, loading: resendInviteEmailLoading } = useResendInviteEmailMutationHook();

  // Get the user email
  const userData = useUserData(userId?.toString());

  const handleSendInviteEmail = () => {
    // Check if the user has received an invite email in the last hour
    if(userData?.lastEmailSentAt) {
      const lastEmailSentAt = new Date(userData.lastEmailSentAt);
      const now = new Date();
      const hoursDiff = differenceInHours(now, lastEmailSentAt);
      if(hoursDiff < 1) {
        return toast(CustomToastComponent, {
          toastId: "send-invite-email-error",
          data: {
            title: t("toasts:toasts.sendinviteemailerror"),
            type: "error",
          }
        });
      }
    }

    resendInviteEmailMutation({
      variables: {
        userId: Number(userId)
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>{t("sendinviteemail.title")}</DialogTitle>
        <DialogHeader>
          <DialogDescription>{t("sendinviteemail.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center">
          <Input
            type="email"
            value={userData?.email}
            disabled
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant={"outline"}>{t("common:buttons.close")}</Button>
          </DialogClose>
          <Button type="submit" className="gap-2" disabled={resendInviteEmailLoading} onClick={handleSendInviteEmail}>
            <CircleLoader visible={resendInviteEmailLoading} height={16} width={16} strokeWidth={2} />
            {t("sendinviteemail.sendinviteemail")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendInviteDialog;
